import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RemoveFromCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  companyName: string;
  /** True while the removal request is running — disables the buttons. */
  confirming: boolean;
  onConfirm: () => void;
}

/**
 * Shared confirmation modal for "Remove user from company" on both
 * /settings/users and /platform/companies/:id/users. Explains that this
 * only revokes company access — the account itself is not deleted.
 */
export function RemoveFromCompanyDialog({
  open,
  onOpenChange,
  userEmail,
  companyName,
  confirming,
  onConfirm,
}: RemoveFromCompanyDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (confirming) return; // don't allow dismissing mid-request
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {userEmail} from {companyName}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              <ul className="list-disc space-y-1.5 pl-4 text-left">
                <li>
                  This person immediately loses access to <span className="font-medium text-foreground">{companyName}</span> —
                  its data, work orders and inventory.
                </li>
                <li>
                  Their account is <span className="font-medium text-foreground">not deleted</span>: they keep access to any
                  other companies they belong to, and can be re-added to this company at any time.
                </li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={confirming}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: "destructive" }))}
            disabled={confirming}
            onClick={(e) => {
              // Keep the dialog open until the request resolves; the page
              // closes it via onOpenChange once the removal finishes.
              e.preventDefault();
              onConfirm();
            }}
          >
            {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirming ? "Removing…" : "Remove from company"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
